import React, { useMemo, useCallback } from 'react';
import clsx from 'clsx';

import './OtpInput.scss';

const RE_DIGIT = new RegExp(/^\d+$/);
const RE_LETTER = new RegExp(/^[a-zA-Z]+$/);

const OtpInput = ({
  value,
  valueLength,
  onChange,
  disabled,
  isDigit = false,
  isLetters = false,
  isError = false,
  errorText,
  onBlur,
}) => {
  const isValueValid = useCallback(
    (value) => {
      if (isDigit && RE_DIGIT.test(value)) {
        return value;
      }

      if (isLetters && RE_LETTER.test(value)) {
        return value;
      }

      return '';
    },
    [isDigit, isLetters],
  );

  const valueItems = useMemo(() => {
    const valueArray = value.split('') || [];

    return [...valueArray, ...(valueLength - valueArray?.length > 0 ? Array(valueLength - valueArray?.length) : [])].map((char) => isValueValid(char));
  }, [value, valueLength, isDigit, isLetters]);

  const handleFocusNextInput = useCallback((target) => {
    const nextElementSibling = target.nextElementSibling;

    if (nextElementSibling) {
      nextElementSibling.focus();
    }
  }, []);

  const handleFocusPrevInput = useCallback((target) => {
    const previousElementSibling = target.previousElementSibling;

    if (previousElementSibling) {
      previousElementSibling.focus();
    }
  }, []);

  const handleFocus = useCallback((event) => {
    const target = event.target;
    const prevInputEl = target.previousElementSibling;

    if (prevInputEl && prevInputEl.value === '') {
      return prevInputEl.focus();
    }

    target.setSelectionRange(0, target.value.length);
  }, []);

  const handleInputChange = useCallback(
    (event, idx) => {
      const target = event.target;
      let targetValue = target.value.trim();
      const isValid = isValueValid(targetValue);

      if (!isValid && targetValue !== '') {
        return;
      }

      targetValue = isValid ? targetValue : ' ';
      const targetValueLength = targetValue.length;

      if (targetValueLength === 1) {
        const newValue = value.substring(0, idx) + targetValue + value.substring(idx + 1);

        onChange(newValue);

        if (!isValid) {
          return;
        }

        handleFocusNextInput(target);
      } else if (targetValueLength === valueLength) {
        onChange(targetValue);
        target.blur();
      }
    },
    [isValueValid, handleFocusNextInput, onChange, valueLength, value],
  );

  const handleKeyDown = useCallback(
    (event) => {
      const { key, target } = event;

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        event.preventDefault();
        return handleFocusNextInput(target);
      }

      if (key === 'ArrowLeft' || key === 'ArrowUp') {
        event.preventDefault();
        return handleFocusPrevInput(target);
      }

      const targetValue = target.value;
      target.setSelectionRange(0, targetValue.length);

      if (event.key !== 'Backspace' || targetValue !== '') {
        return;
      }

      handleFocusPrevInput(target);
    },
    [handleFocusNextInput, handleFocusPrevInput],
  );

  return (
    <div className='otp-wrapper'>
      <div className='otp-group'>
        {valueItems.map((digit, idx) => (
          <input
            key={idx}
            type='text'
            inputMode='numeric'
            autoComplete='one-time-code'
            pattern='\d{1}'
            maxLength={valueLength}
            className={clsx('otp-input', isError && 'otp-input--error')}
            value={digit}
            disabled={disabled}
            onChange={(e) => handleInputChange(e, idx)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={onBlur}
          />
        ))}
      </div>

      {errorText ? <span className='otp-error'>{errorText}</span> : null}
    </div>
  );
};

export default OtpInput;
